import React, { useState, useCallback, useEffect } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { AlertCircle } from 'lucide-react';
import { DataTable } from '../ui/data-table';

const fmt = (v) =>
  Number(v || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

const RouteMarginChart = () => {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/api/finance/routes-summary', { params: { year } });
      setData(res.data);
    } catch {
      setError('Impossible de charger les données par route.');
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const routes = data?.routes || [];

  const chartData = routes.map((r) => ({
    name: r.route,
    Profit: r.profit,
  }));

  return (
    <div className="space-y-4">
      {/* Year selector */}
      <div className="flex items-center gap-2">
        {years.map((y) => (
          <Button
            key={y}
            size="sm"
            variant={y === year ? 'default' : 'outline'}
            onClick={() => setYear(y)}
          >
            {y}
          </Button>
        ))}
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-center py-8 text-muted-foreground">Chargement...</div>
      ) : routes.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">Aucune donnée pour {year}</div>
      ) : (
        <>
          {/* Horizontal bar chart */}
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="text-base">Profit par route — {year}</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={Math.max(200, routes.length * 50)}>
                <BarChart
                  data={chartData}
                  layout="vertical"
                  margin={{ top: 4, right: 16, left: 0, bottom: 0 }}
                >
                  <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}€`} />
                  <YAxis
                    type="category"
                    dataKey="name"
                    tick={{ fontSize: 11 }}
                    width={160}
                  />
                  <Tooltip formatter={(value) => fmt(value)} contentStyle={{ borderRadius: '8px', border: '1px solid #ECE7DC', fontSize: '13px' }} />
                  <Bar dataKey="Profit" radius={[0, 4, 4, 0]}>
                    {chartData.map((entry, idx) => (
                      <Cell
                        key={idx}
                        fill={entry.Profit >= 0 ? '#0E8855' : '#ef4444'}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Detail table */}
          <DataTable
            data={routes}
            searchPlaceholder="Rechercher une route..."
            emptyMessage="Aucune route"
            pageSize={10}
            columns={[
              {
                accessorKey: 'route',
                header: 'Route',
                meta: { cellClassName: 'font-medium text-foreground' },
              },
              {
                accessorKey: 'shipment_count',
                header: 'Départs',
                meta: { className: 'text-right', cellClassName: 'text-right' },
              },
              {
                accessorKey: 'total_weight',
                header: 'Poids (kg)',
                cell: ({ getValue }) => <span>{getValue().toFixed(1)}</span>,
                meta: { className: 'text-right', cellClassName: 'text-right' },
              },
              {
                accessorKey: 'total_revenue',
                header: 'Revenu',
                cell: ({ getValue }) => fmt(getValue()),
                meta: { className: 'text-right', cellClassName: 'text-right' },
              },
              {
                accessorKey: 'total_charges',
                header: 'Charges',
                cell: ({ getValue }) => fmt(getValue()),
                meta: { className: 'text-right', cellClassName: 'text-right' },
              },
              {
                accessorKey: 'profit',
                header: 'Profit',
                cell: ({ getValue }) => {
                  const v = getValue();
                  return (
                    <span className={`font-semibold ${v >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {fmt(v)}
                    </span>
                  );
                },
                meta: { className: 'text-right', cellClassName: 'text-right' },
              },
              {
                accessorKey: 'margin_pct',
                header: 'Marge %',
                cell: ({ getValue }) => {
                  const v = getValue();
                  const color = v >= 20 ? 'text-green-600' : v > 0 ? 'text-amber-600' : 'text-red-600';
                  return <span className={`font-medium ${color}`}>{v}%</span>;
                },
                meta: { className: 'text-right', cellClassName: 'text-right' },
              },
            ]}
          />
        </>
      )}
    </div>
  );
};

export default RouteMarginChart;
